import type { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { isPostCodeRequiredForCountry } from '@/shared/config/countries'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { InstallationFormRequiredMark } from '@/features/installations/components/installation-create/installation-form-required-mark'
import { InstallationFormMessage } from '@/features/installations/components/installation-form-message'
import type { InstallationCreateFormInput } from '@/features/installations/installation-create.schema'

type InstallationCreateStepAddressLocalProps = {
  form: UseFormReturn<InstallationCreateFormInput>
}

export function InstallationCreateStepAddressLocal({
  form,
}: InstallationCreateStepAddressLocalProps) {
  const { t } = useTranslation('installations')
  const postCodeRequired = isPostCodeRequiredForCountry(form.getValues('country') ?? '')

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('create.sections.addressLocal')}</CardTitle>
        <CardDescription>{t('create.sections.addressLocalHint')}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-4 sm:col-span-2 sm:flex-row sm:items-start">
          <FormField
            control={form.control}
            name="localStreet"
            render={({ field }) => (
              <FormItem className="min-w-0 flex-1">
                <FormLabel>
                  {t('create.fields.localStreet.label')}
                  <InstallationFormRequiredMark />
                </FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <InstallationFormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="localStreetNumber"
            render={({ field }) => (
              <FormItem className="w-full shrink-0 sm:w-40">
                <FormLabel>
                  {t('create.fields.localStreetNumber.label')}
                  <InstallationFormRequiredMark />
                </FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <InstallationFormMessage />
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name="localPostCode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('create.fields.localPostCode.label')}
                {postCodeRequired ? <InstallationFormRequiredMark /> : null}
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <InstallationFormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="localCity"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('create.fields.localCity.label')}
                <InstallationFormRequiredMark />
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <InstallationFormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="localPoBox"
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel>{t('create.fields.localPoBox.label')}</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <InstallationFormMessage />
            </FormItem>
          )}
        />
      </CardContent>
    </Card>
  )
}
